// src/components/AdminPanel.js
import React, { useState, useEffect, useCallback } from 'react'; // Adicione useCallback
import { 
  collection, 
  getDocs, 
  doc, 
  updateDoc, 
  deleteDoc,
  query, 
  where
} from 'firebase/firestore';
import { db, functions } from '../firebase';
import { useUser } from '../context/UserContext';
import { httpsCallable } from 'firebase/functions';

const AdminPanel = () => {
  const { user } = useUser();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchEmail, setSearchEmail] = useState('');
  const [message, setMessage] = useState('');
  const [stats, setStats] = useState({ total: 0, free: 0, premium: 0 });

  // estados para adicionar usuario manualmente
  const [showAddForm, setShowAddForm] = useState(false);
  const [newUserData, setNewUserData] = useState({ name: '', email: '', password: '', confirmPassword: '' });
  const [addingUser, setAddingUser] = useState(false);

  // Lista de emails admin (APENAS SEU EMAIL)
  const adminEmails = ['chfernan12@gmail.com'];

  // Verificar se é admin
  const isAdmin = user && adminEmails.includes(user.email);

  // Atualizar estatísticas
  const updateStats = useCallback((usersList) => {
    const total = usersList.length;
    const free = usersList.filter(u => u.plan === 'free').length;
    const premium = usersList.filter(u => u.plan === 'prem').length;
    
    setStats({ total, free, premium });
  }, []);

  // Buscar todos os usuários - AGORA COM useCallback
  const fetchUsers = useCallback(async () => {
    if (!isAdmin) return;
    
    setLoading(true);
    try {
      const usersRef = collection(db, 'users');
      const querySnapshot = await getDocs(usersRef);
      
      let usersList = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate?.() || new Date()
      }));

      // ordenar por data localmente para evitar dependência de índice
      usersList.sort((a, b) => b.createdAt - a.createdAt);
      
      setUsers(usersList);
      updateStats(usersList);
    } catch (error) {
      console.error('Erro ao buscar usuários:', error);
      setMessage('❌ Erro ao carregar usuários');
    } finally {
      setLoading(false);
    }
  }, [isAdmin, updateStats]); // Adicione as dependências

  // Buscar usuário por email
  const searchUser = async () => {
    if (!isAdmin) return;
    
    if (!searchEmail.trim()) {
      fetchUsers();
      return;
    }

    setLoading(true);
    try {
      const usersRef = collection(db, 'users');
      const q = query(usersRef, where('email', '==', searchEmail.toLowerCase().trim()));
      const querySnapshot = await getDocs(q);
      
      const usersList = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate?.() || new Date()
      }));
      
      setUsers(usersList);
      updateStats(usersList);
      setMessage(usersList.length === 0 ? '🔍 Nenhum usuário encontrado' : '');
    } catch (error) {
      console.error('Erro ao buscar usuário:', error);
      setMessage('❌ Erro na busca');
    } finally {
      setLoading(false);
    }
  };

  // Atualizar plano do usuário
  const updateUserPlan = async (userId, newPlan, userEmail) => {
    if (!isAdmin) return;
    
    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        plan: newPlan,
        planUpdatedAt: new Date(),
        updatedBy: user.email
      });
      
      setMessage(`✅ ${userEmail} atualizado para ${newPlan === 'prem' ? 'PREMIUM' : 'FREE'}`);
      fetchUsers();
    } catch (error) {
      console.error('Erro ao atualizar plano:', error);
      setMessage('❌ Erro ao atualizar plano');
    }
  };

  // Alternar ativo/inativo
  const toggleActive = async (userId, currentActive, userEmail) => {
    if (!isAdmin) return;
    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        active: currentActive === false ? true : false,
        updatedBy: user.email,
        updatedAt: new Date()
      });
      setMessage(`✅ ${userEmail} agora está ${currentActive === false ? 'ATIVO' : 'INATIVO'}`);
      fetchUsers();
    } catch (error) {
      console.error('Erro ao alternar active:', error);
      setMessage('❌ Erro ao atualizar status');
    }
  };

  // Remover usuário (apenas documento Firestore)
  const deleteUser = async (userId, userEmail) => {
    if (!isAdmin) return;
    const confirm = window.confirm(`Confirmar remoção do usuário ${userEmail} (apenas Firestore)?`);
    if (!confirm) return;

    try {
      const userRef = doc(db, 'users', userId);
      await deleteDoc(userRef);
      setMessage(`✅ ${userEmail} removido do Firestore`);
      fetchUsers();
    } catch (error) {
      console.error('Erro ao remover usuário:', error);
      setMessage('❌ Erro ao remover usuário');
    }
  };

  useEffect(() => {
    if (isAdmin) {
      fetchUsers();
    }
  }, [isAdmin, fetchUsers]); // Adicione fetchUsers nas dependências

  // ------------ funcoes de adicao manual de usuario --------------
  const handleChangeNewUser = (e) => {
    setNewUserData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    setMessage('');

    // validações simples
    if (!newUserData.name || !newUserData.email || !newUserData.password || !newUserData.confirmPassword) {
      setMessage('❌ Todos os campos do formulário devem ser preenchidos');
      return;
    }
    if (newUserData.password !== newUserData.confirmPassword) {
      setMessage('❌ Senhas não coincidem');
      return;
    }
    if (newUserData.password.length < 6) {
      setMessage('❌ A senha deve ter pelo menos 6 caracteres');
      return;
    }

    setAddingUser(true);
    try {
      const createAuthUser = httpsCallable(functions, 'createAuthUser');
      const resp = await createAuthUser({
        name: newUserData.name,
        email: newUserData.email.toLowerCase().trim(),
        password: newUserData.password
      });
      setMessage(`✅ Usuário ${newUserData.email} criado com UID ${resp.data.uid}`);
      setNewUserData({ name: '', email: '', password: '', confirmPassword: '' });
      setShowAddForm(false);
      fetchUsers();
    } catch (err) {
      console.error('Erro ao criar usuário manualmente:', err);
      setMessage('❌ ' + (err.message || 'Erro desconhecido'));      
    } finally {
      setAddingUser(false);
    }
  };

  // Se não for admin, mostrar mensagem de acesso negado
  if (!isAdmin) {
    return (
      <div className="container mt-5">
        <div className="row justify-content-center">
          <div className="col-md-6">
            <div className="card text-center">
              <div className="card-body">
                <div className="text-danger mb-3">
                  <i className="fas fa-ban fa-3x"></i>
                </div>
                <h3>Acesso Negado</h3>
                <p className="text-muted">
                  Você não tem permissão para acessar o painel administrativo.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mt-4">
      <div className="card shadow">
        <div className="card-header bg-dark text-white">
          <div className="d-flex justify-content-between align-items-center">
            <h4 className="mb-0">👑 Painel Admin - Gerenciar Planos</h4>
            <span className="badge bg-primary">Admin</span>
          </div>
        </div>
        
        <div className="card-body">
          {/* botão e formulário de adicionar usuário manual */}
          <div className="row mb-3">
            <div className="col-12 text-end">
              <button
                className="btn btn-sm btn-outline-success"
                onClick={() => setShowAddForm(prev => !prev)}
              >
                {showAddForm ? '✖️ Cancelar' : '➕ Adicionar Usuário'}
              </button>
            </div>
          </div>
          {showAddForm && (
            <div className="row mb-4">
              <div className="col-12">
                <div className="card border-success">
                  <div className="card-body">
                    <h5 className="card-title">Cadastro Manual de Usuário</h5>
                    <form onSubmit={handleAddUser}>
                      <div className="row g-2">
                        <div className="col-md-4">
                          <input
                            type="text"
                            name="name"
                            className="form-control"
                            placeholder="Nome completo"
                            value={newUserData.name}
                            onChange={handleChangeNewUser}
                          />
                        </div>
                        <div className="col-md-4">
                          <input
                            type="email"
                            name="email"
                            className="form-control"
                            placeholder="E-mail"
                            value={newUserData.email}
                            onChange={handleChangeNewUser}
                          />
                        </div>
                        <div className="col-md-2">
                          <input
                            type="password"
                            name="password"
                            className="form-control"
                            placeholder="Senha"
                            value={newUserData.password}
                            onChange={handleChangeNewUser}
                          />
                        </div>
                        <div className="col-md-2">
                          <input
                            type="password"
                            name="confirmPassword"
                            className="form-control"
                            placeholder="Confirmação"
                            value={newUserData.confirmPassword}
                            onChange={handleChangeNewUser}
                          />
                        </div>
                      </div>
                      <div className="mt-2 text-end">
                        <button
                          type="submit"
                          className="btn btn-success btn-sm"
                          disabled={addingUser}
                        >
                          {addingUser ? 'Criando...' : 'Criar'}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Estatísticas */}
          <div className="row mb-4">
            <div className="col-md-4">
              <div className="card text-center bg-primary text-white">
                <div className="card-body">
                  <h5>Total Usuários</h5>
                  <h2>{stats.total}</h2>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card text-center bg-secondary text-white">
                <div className="card-body">
                  <h5>Usuários Free</h5>
                  <h2>{stats.free}</h2>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card text-center bg-success text-white">
                <div className="card-body">
                  <h5>Usuários Premium</h5>
                  <h2>{stats.premium}</h2>
                </div>
              </div>
            </div>
          </div>

          {/* Busca */}
          <div className="row mb-4">
            <div className="col-md-8">
              <div className="input-group">
                <input
                  type="email"
                  className="form-control"
                  placeholder="Buscar usuário por email..."
                  value={searchEmail}
                  onChange={(e) => setSearchEmail(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && searchUser()}
                />
                <button 
                  className="btn btn-primary"
                  onClick={searchUser}
                  disabled={loading}
                >
                  {loading ? '🔍 Buscando...' : '🔍 Buscar'}
                </button>
              </div>
            </div>
            <div className="col-md-4 text-end">
              <button 
                className="btn btn-outline-secondary"
                onClick={fetchUsers}
                disabled={loading}
              >
                🔄 Atualizar Lista
              </button>
            </div>
          </div>

          {message && (
            <div className={`alert ${message.includes('✅') ? 'alert-success' : 'alert-danger'}`}>
              {message}
            </div>
          )}

          {/* Lista de Usuários */}
          <div className="table-responsive">
            <table className="table table-striped table-hover">
              <thead className="table-dark">
                <tr>
                  <th>Nome</th>
                  <th>Email</th>
                  <th>Plano</th>
                  <th>Cadastro</th>
                  <th>Último Login</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="6" className="text-center py-4">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Carregando...</span>
                      </div>
                      <p className="mt-2 mb-0">Carregando usuários...</p>
                    </td>
                  </tr>
                ) : users.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="text-center text-muted py-4">
                      {searchEmail ? 'Nenhum usuário encontrado' : 'Nenhum usuário cadastrado'}
                    </td>
                  </tr>
                ) : (
                  users.map(user => (
                    <tr key={user.id}>
                      <td>
                        <strong>{user.name || 'N/A'}</strong>
                      </td>
                      <td>
                        <code>{user.email}</code>
                      </td>
                      <td>
                        <span className={`badge ${user.plan === 'prem' ? 'bg-success' : 'bg-secondary'}`}>
                          {user.plan === 'prem' ? '⭐ PREMIUM' : 'FREE'}
                        </span>
                      </td>
                      <td>
                        {user.createdAt.toLocaleDateString('pt-BR')}
                      </td>
                      <td>
                        {user.lastLogin?.toDate?.().toLocaleDateString('pt-BR') || 'N/A'}
                      </td>
                      <td>
                        <div className="d-flex gap-2 align-items-center">
                          <div className="form-check">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id={`active-${user.id}`}
                              checked={user.active !== false}
                              onChange={() => toggleActive(user.id, user.active, user.email)}
                            />
                            <label className="form-check-label" htmlFor={`active-${user.id}`}>
                              {user.active === false ? 'Inativo' : 'Ativo'}
                            </label>
                          </div>

                          {user.plan === 'free' ? (
                            <button
                              className="btn btn-success btn-sm"
                              onClick={() => updateUserPlan(user.id, 'prem', user.email)}
                              title="Tornar usuário Premium"
                            >
                              ⬆️ Tornar Premium
                            </button>
                          ) : (
                            <button
                              className="btn btn-warning btn-sm"
                              onClick={() => updateUserPlan(user.id, 'free', user.email)}
                              title="Rebaixar para Free"
                            >
                              ⬇️ Rebaixar para Free
                            </button>
                          )}

                          <button
                            className="btn btn-danger btn-sm"
                            onClick={() => deleteUser(user.id, user.email)}
                            title="Remover usuário"
                          >
                            🗑️
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Informações do Admin */}
          <div className="card mt-4 bg-light">
            <div className="card-body">
              <h6>💡 Informações do Painel:</h6>
              <ul className="mb-0">
                <li>Total de usuários: <strong>{stats.total}</strong></li>
                <li>Usuários free: <strong>{stats.free}</strong></li>
                <li>Usuários premium: <strong>{stats.premium}</strong></li>
                <li>Taxa de conversão: <strong>{stats.total > 0 ? ((stats.premium / stats.total) * 100).toFixed(1) : 0}%</strong></li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;